import { ICellRendererParams } from 'ag-grid-community';

import './style.scss';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { NextWorkflowError } from 'models/NextWorkflowError';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Pricing } from 'routes/smart-quote/components/prompt-results/components/price-button/pricing';
import SuccessfulPriceButton from 'routes/smart-quote/components/prompt-results/components/price-button/successful-price-button';
import UnsuccessfulPriceButton from 'routes/smart-quote/components/prompt-results/components/price-button/unsuccessful-price-button';
import { useRepriceQuotes } from 'routes/smart-quote/hooks/use-reprice-quotes';
import useSaveDisplayedPrices from 'routes/smart-quote/hooks/use-save-displayed-prices';
import { trackSmartQuoteEvent } from 'routes/smart-quote/utils';
import { getQuoteRejectionsByQuoteId, getQuoteWarningsByQuoteId } from 'state/selectors';
import { SmartQuoteParsingResult } from 'state/workspaces/smart-quote/model';

function useGetDisplayPriceLabel(data?: SmartQuoteParsingResult): string {
  const { formatMessage, formatNumber } = useIntl();
  if (!data) {
    return '';
  }
  const { quoteStatus, solveResult, solveResultFormat, status } = data;

  if (quoteStatus === 'Rejected') {
    return formatMessage({ id: 'smartQuote.workspace.main.priceButton.rejected' });
  }
  if (quoteStatus === 'Failed' || status === 'Error') {
    return formatMessage({ id: 'smartQuote.workspace.main.priceButton.failed' });
  }
  if (quoteStatus === 'Quoted' && solveResult !== undefined) {
    return `${formatNumber(solveResult, { maximumFractionDigits: 4 })}${solveResultFormat === 'Percent' ? ' %' : ''}`;
  }
  return formatMessage({ id: 'smartQuote.workspace.main.priceButton.generating' });
}

type PriceButtonProps = ICellRendererParams<SmartQuoteParsingResult>;

const PriceButton = ({ data }: PriceButtonProps) => {
  const priceLabel = useGetDisplayPriceLabel(data);
  const { reloadInAdvanced } = useRepriceQuotes();
  const tabId = useAssertedActiveTabId();
  const saveDisplayedPrices = useSaveDisplayedPrices();

  const {
    confidence = 0,
    promptId = '',
    id: resultId = '',
    rfqId = '',
    externalQuoteId = '',
    quoteStatus,
    error,
    productKey,
    isDisplayedPrice = false,
  } = data ?? {};

  const errors = useAppSelector(state => getQuoteRejectionsByQuoteId(state, rfqId));
  const warnings = useAppSelector(state => getQuoteWarningsByQuoteId(state, rfqId));

  const openInAdvancedWorkspace = useCallback(() => {
    if (quoteStatus === 'Quoted') {
      saveDisplayedPrices({
        tabId,
        promptId,
        displayedPrices: [{ resultId: parseInt(resultId), isDisplayedPrice: true }],
      });
    }
    reloadInAdvanced(rfqId, externalQuoteId);
    trackSmartQuoteEvent('table actions', 'open in advanced workspace');
  }, [rfqId, externalQuoteId, reloadInAdvanced, saveDisplayedPrices, tabId, resultId, quoteStatus, promptId]);

  if (error) {
    return (
      <UnsuccessfulPriceButton
        label={priceLabel}
        errors={[
          { originMessage: error.message, rejectionMessage: error.errorDescription, rejectionCode: error.errorCode },
        ]}
        warnings={warnings}
        productSubtype={productKey?.instrument?.contractName}
      />
    );
  }
  switch (quoteStatus) {
    case 'Failed':
    case 'Rejected':
      return (
        <UnsuccessfulPriceButton
          label={priceLabel}
          errors={errors as NextWorkflowError[]}
          warnings={warnings}
          productSubtype={productKey?.instrument?.contractName}
          onClick={openInAdvancedWorkspace}
        />
      );
    case 'Quoted':
      return (
        <SuccessfulPriceButton
          confidence={confidence}
          onClick={openInAdvancedWorkspace}
          label={priceLabel}
          isDisplayedPrice={isDisplayedPrice}
        />
      );
    default:
      return <Pricing label={priceLabel} />;
  }
};

export default PriceButton;
